import {autoserializeAs, inheritSerialization} from "dcerialize";
import type {Existing, IdentifiableModel, IdOf, New} from "../base/IdentifiableModel";
import {createExisting, createNew} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import type {SoftDeletableModel} from "../base/SoftDeletableModel";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyCustomerLocation, SimpleCustomerLocation} from "./CustomerLocation";

export class IdOnlyCustomerDepartment implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyCustomerDepartment>): Existing<IdOnlyCustomerDepartment> {
    return createExisting(IdOnlyCustomerDepartment, id);
  }
}

@inheritSerialization(() => IdOnlyCustomerDepartment)
export class CreatableCustomerDepartment extends IdOnlyCustomerDepartment {
  @autoserializeAs(() => String)
  public name: string;

  public static createNew(name: string): New<CreatableCustomerDepartment>;
  public static createNew(name: string, location: Readonly<Existing<IdOnlyCustomerLocation>>): New<WritableCustomerDepartment>;
  public static createNew(name: string, location?: Readonly<Existing<IdOnlyCustomerLocation>>): New<CreatableCustomerDepartment | WritableCustomerDepartment> {
    const dept = location
      ? Object.assign(createNew(WritableCustomerDepartment), {location: location})
      : createNew(CreatableCustomerDepartment);
    dept.name = name;
    return dept;
  }
}

@inheritSerialization(() => CreatableCustomerDepartment)
export class WritableCustomerDepartment extends CreatableCustomerDepartment {
  @autoserializeAs(() => IdOnlyCustomerLocation)
  public readonly location: Readonly<Existing<IdOnlyCustomerLocation>>;
}

@inheritSerialization(() => WritableCustomerDepartment)
export class SimpleCustomerDepartment extends WritableCustomerDepartment implements SoftDeletableModel {
  @autoserializeAs(() => SimpleCustomerLocation)
  public override readonly location: Readonly<Existing<SimpleCustomerLocation>>;
  @autoserializeAs(() => Boolean)
  public readonly isPredefined: boolean;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;
}

@inheritSerialization(() => SimpleCustomerDepartment)
export class CustomerDepartment extends SimpleCustomerDepartment implements ModificationTrackedModel {
  @autoserializeAs(() => Number)
  public readonly numberOfEquipment: number;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}
